/**
 * Stable table sort by `{k, d}` (plan §3.3).
 *
 * Spread before sort, never in place — `Array.prototype.sort` mutates, and the
 * universe is frozen, so an in-place sort is a runtime throw in strict mode and
 * a silent cross-screen reorder without it.
 *
 * `risk` is a QUERY-RELATIVE derivation and therefore arrives as an injected
 * map rather than being read off the row (ADR-0004): the caller must have
 * computed it over the same filtered set it is sorting, and passing it
 * explicitly is what makes that dependency visible at the call site.
 */
import type { SortKey, SortState, Ticker, WindowKey } from '@meridian/contracts';

import { COLLATOR } from '../constants/collation';

export interface SortableRow {
  readonly ticker: Ticker;
  readonly name: string;
  readonly sector: string;
  readonly mcap: number | null;
  readonly vol: number | null;
  readonly beta: number | null;
  readonly ret: Readonly<Record<WindowKey, number | null>>;
  readonly dd: Readonly<Record<WindowKey, number | null>>;
}

export interface SortContext {
  readonly window: WindowKey;
  readonly riskScores?: ReadonlyMap<Ticker, number> | undefined;
}

type Accessor = (row: SortableRow, ctx: SortContext) => number | string | null;

const ACCESSORS: Readonly<Record<SortKey, Accessor>> = {
  ticker: (r) => r.ticker,
  name: (r) => r.name,
  sector: (r) => r.sector,
  ret: (r, c) => r.ret[c.window],
  dd: (r, c) => r.dd[c.window],
  vol: (r) => r.vol,
  beta: (r) => r.beta,
  mcap: (r) => r.mcap,
  risk: (r, c) => c.riskScores?.get(r.ticker) ?? null,
};

/**
 * Nulls always sort LAST regardless of direction. Flipping them with the
 * direction would make "worst drawdown first" put every unreported row at the
 * top of the screen — the rows with no drawdown to report.
 */
function compare(a: number | string | null, b: number | string | null, dir: number): number {
  if (a === null) return b === null ? 0 : 1;
  if (b === null) return -1;
  if (typeof a === 'string' && typeof b === 'string') return COLLATOR.compare(a, b) * dir;
  return ((a as number) - (b as number)) * dir;
}

export function sortRows<T extends SortableRow>(
  rows: readonly T[],
  sort: SortState,
  context: SortContext,
): readonly T[] {
  const accessor = ACCESSORS[sort.k];
  return [...rows].sort(
    (a, b) =>
      compare(accessor(a, context), accessor(b, context), sort.d) ||
      /* Ticker ascending in BOTH directions — determinism, not a second sort dim. */
      COLLATOR.compare(a.ticker, b.ticker),
  );
}
